import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  ENUM_HelpCaseType,
  ENUM_MortgagePartStepType,
  ENUM_PropertyInvestmentType,
  ENUM_SmartSearchPropertyPurchaseType,
  ENUM_SmartSearchRemortgageReason,
} from '@api-new/common';
import { propertyPriceValidator } from '@client-ui/components/forms/smart-search-new-property-basic-details-form/utils/form-utils';
import { SmartSearchType } from '@platform/smart-search/enums/smart-search-type.enum';
import { AddSmartSearchPropertyMortgageSelectItem } from '@platform/smart-search/models/add-smart-search-property-mortgage-select-item.model';
import { MAX_MORTGAGE_DURATION_IN_MONTHS, MIN_MORTGAGE_DURATION_IN_MONTHS } from '@shared-lib/constants/mortgage';
import { UK_PHONE_NUMBER_REGEX } from '@shared-lib/constants/phone-number';
import { FormRawValue } from '@shared-lib/models/form-raw-value.model';

export interface AddSmartSearchDialogFormTypeSelection {
  type: FormControl<SmartSearchType>;
}

export interface AddSmartSearchDialogFormPropertySelection {
  propertyMortgageSelectItem: FormControl<AddSmartSearchPropertyMortgageSelectItem>;
}

export interface AddSmartSearchDialogFormNewPropertyPurchaseTypeSelection {
  type: FormControl<ENUM_SmartSearchPropertyPurchaseType>;
}

export interface AddSmartSearchDialogFormNewPropertyMaximumPrice {
  propertyPrice: FormControl<number>;
  deposit: FormControl<number>;
  newTermInMonths: FormControl<number>;
  ageInYears: FormControl<number>;
}

export interface AddSmartSearchDialogFormRemortgageBorrowMore {
  loanAmountIncrease: FormControl<number>;
  reason: FormControl<ENUM_SmartSearchRemortgageReason>;
  termInMonths: FormControl<number>;
}

export interface AddSmartSearchDialogFormRemortgagePayMortgageFaster {
  monthlyPaymentIncrease: FormControl<number>;
  termInMonths: FormControl<number>;
}

export interface AddSmartSearchDialogFormSpecifySearch {
  initialInterestRateType: FormControl<ENUM_MortgagePartStepType[]>;
  comparisonPeriod: FormControl<number[]>;
  smartSearchName: FormControl<string>;
}

export interface AddSmartSearchDialogFormRemortgageSpeakToAnExpert {
  helpCaseName: FormControl<string>;
  type: FormControl<ENUM_HelpCaseType>;
  description: FormControl<string>;
  phoneNumber: FormControl<string>;
}

export interface AddSmartSearchDialogFormNewPropertyInvestmentTypeSelection {
  type: FormControl<ENUM_PropertyInvestmentType>;
}

export interface AddSmartSearchDialogForm {
  typeSelection: FormGroup<AddSmartSearchDialogFormTypeSelection>;
  propertySelection: FormGroup<AddSmartSearchDialogFormPropertySelection>;
  newPropertyPurchaseTypeSelection: FormGroup<AddSmartSearchDialogFormNewPropertyPurchaseTypeSelection>;
  newPropertyMaximumPrice: FormGroup<AddSmartSearchDialogFormNewPropertyMaximumPrice>;
  remortgageBorrowMore: FormGroup<AddSmartSearchDialogFormRemortgageBorrowMore>;
  remortgagePayMortgageFaster: FormGroup<AddSmartSearchDialogFormRemortgagePayMortgageFaster>;
  specifySearch: FormGroup<AddSmartSearchDialogFormSpecifySearch>;
  remortgageSpeakToAnExpert: FormGroup<AddSmartSearchDialogFormRemortgageSpeakToAnExpert>;
  newPropertyInvestmentTypeSelection: FormGroup<AddSmartSearchDialogFormNewPropertyInvestmentTypeSelection>;
}

export type AddSmartSearchDialogFormRawValues = FormRawValue<FormGroup<AddSmartSearchDialogForm>>;

export const defaultAddSmartSearchDialogValue: FormRawValue<FormGroup<AddSmartSearchDialogForm>> = {
  typeSelection: {
    type: null,
  },
  propertySelection: {
    propertyMortgageSelectItem: null,
  },
  newPropertyPurchaseTypeSelection: {
    type: null,
  },
  newPropertyMaximumPrice: {
    propertyPrice: null,
    deposit: null,
    newTermInMonths: MIN_MORTGAGE_DURATION_IN_MONTHS,
    ageInYears: null,
  },
  remortgageBorrowMore: {
    loanAmountIncrease: 25000,
    reason: null,
    termInMonths: null,
  },
  remortgagePayMortgageFaster: {
    monthlyPaymentIncrease: null,
    termInMonths: null,
  },
  specifySearch: {
    initialInterestRateType: [
      ENUM_MortgagePartStepType.MORTGAGE_PART_STEP_TYPE_FIXED,
      ENUM_MortgagePartStepType.MORTGAGE_PART_STEP_TYPE_VARIABLE,
    ],
    comparisonPeriod: [2, 4],
    smartSearchName: null,
  },
  remortgageSpeakToAnExpert: {
    helpCaseName: null,
    type: null,
    description: null,
    phoneNumber: null,
  },
  newPropertyInvestmentTypeSelection: {
    type: null,
  },
};

export const buildAddSmartSearchDialog = (): FormGroup<AddSmartSearchDialogForm> =>
  new FormGroup<AddSmartSearchDialogForm>({
    typeSelection: new FormGroup<AddSmartSearchDialogFormTypeSelection>({
      type: new FormControl<SmartSearchType>(defaultAddSmartSearchDialogValue.typeSelection.type, Validators.required),
    }),
    propertySelection: new FormGroup<AddSmartSearchDialogFormPropertySelection>({
      propertyMortgageSelectItem: new FormControl<AddSmartSearchPropertyMortgageSelectItem>(
        defaultAddSmartSearchDialogValue.propertySelection.propertyMortgageSelectItem,
        [Validators.required],
      ),
    }),
    newPropertyPurchaseTypeSelection: new FormGroup<AddSmartSearchDialogFormNewPropertyPurchaseTypeSelection>({
      type: new FormControl<ENUM_SmartSearchPropertyPurchaseType>(
        defaultAddSmartSearchDialogValue.newPropertyPurchaseTypeSelection.type,
        Validators.required,
      ),
    }),
    newPropertyMaximumPrice: new FormGroup<AddSmartSearchDialogFormNewPropertyMaximumPrice>(
      {
        propertyPrice: new FormControl<number>(defaultAddSmartSearchDialogValue.newPropertyMaximumPrice.propertyPrice, Validators.required),
        deposit: new FormControl<number>(defaultAddSmartSearchDialogValue.newPropertyMaximumPrice.deposit, Validators.required),
        newTermInMonths: new FormControl<number>(defaultAddSmartSearchDialogValue.newPropertyMaximumPrice.newTermInMonths, [
          Validators.required,
          Validators.min(MIN_MORTGAGE_DURATION_IN_MONTHS),
          Validators.max(MAX_MORTGAGE_DURATION_IN_MONTHS),
        ]),
        ageInYears: new FormControl<number>(defaultAddSmartSearchDialogValue.newPropertyMaximumPrice.ageInYears, [Validators.min(18)]),
      },
      { validators: [propertyPriceValidator()] },
    ),
    remortgageBorrowMore: new FormGroup<AddSmartSearchDialogFormRemortgageBorrowMore>({
      loanAmountIncrease: new FormControl<number>(defaultAddSmartSearchDialogValue.remortgageBorrowMore.loanAmountIncrease, [
        Validators.required,
        Validators.min(5000),
      ]),
      reason: new FormControl<ENUM_SmartSearchRemortgageReason>(
        defaultAddSmartSearchDialogValue.remortgageBorrowMore.reason,
        Validators.required,
      ),
      termInMonths: new FormControl<number>(defaultAddSmartSearchDialogValue.remortgageBorrowMore.termInMonths, Validators.required),
    }),
    remortgagePayMortgageFaster: new FormGroup<AddSmartSearchDialogFormRemortgagePayMortgageFaster>({
      monthlyPaymentIncrease: new FormControl<number>(
        defaultAddSmartSearchDialogValue.remortgagePayMortgageFaster.monthlyPaymentIncrease,
        Validators.required,
      ),
      termInMonths: new FormControl<number>(defaultAddSmartSearchDialogValue.remortgagePayMortgageFaster.termInMonths, Validators.required),
    }),
    specifySearch: new FormGroup<AddSmartSearchDialogFormSpecifySearch>({
      initialInterestRateType: new FormControl<ENUM_MortgagePartStepType[]>(
        defaultAddSmartSearchDialogValue.specifySearch.initialInterestRateType,
        [Validators.required],
      ),
      comparisonPeriod: new FormControl<number[]>(defaultAddSmartSearchDialogValue.specifySearch.comparisonPeriod, Validators.required),
      smartSearchName: new FormControl<string>(defaultAddSmartSearchDialogValue.specifySearch.smartSearchName, Validators.required),
    }),
    remortgageSpeakToAnExpert: new FormGroup<AddSmartSearchDialogFormRemortgageSpeakToAnExpert>({
      helpCaseName: new FormControl<string>(defaultAddSmartSearchDialogValue.remortgageSpeakToAnExpert.helpCaseName, Validators.required),
      type: new FormControl<ENUM_HelpCaseType>(defaultAddSmartSearchDialogValue.remortgageSpeakToAnExpert.type, Validators.required),
      description: new FormControl<string>(defaultAddSmartSearchDialogValue.remortgageSpeakToAnExpert.description, Validators.required),
      phoneNumber: new FormControl<string>(defaultAddSmartSearchDialogValue.remortgageSpeakToAnExpert.phoneNumber, [
        Validators.required,
        Validators.pattern(UK_PHONE_NUMBER_REGEX),
      ]),
    }),
    newPropertyInvestmentTypeSelection: new FormGroup<AddSmartSearchDialogFormNewPropertyInvestmentTypeSelection>({
      type: new FormControl<ENUM_PropertyInvestmentType>(
        defaultAddSmartSearchDialogValue.newPropertyInvestmentTypeSelection.type,
        Validators.required,
      ),
    }),
  });
